import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Input, Select, Table, Tag } from 'antd';
import { AccessService } from '../../service/api/access-service';

const ACTIONS = [
  'ROLE_CREATE',
  'ROLE_UPDATE',
  'ROLE_MATRIX_SAVE',
  'MODULE_CREATE',
  'MODULE_UPDATE',
  'PAGE_CREATE',
  'PAGE_UPDATE',
  'PERMISSION_CREATE',
  'PERMISSION_UPDATE',
  'SEED',
  'UPGRADE_RBAC_MODULE',
];
const TARGETS = ['Role', 'Module', 'Page', 'Permission', 'Catalog'];

const pretty = json => {
  if (!json) return '—';
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch (_e) {
    return json;
  }
};

/** For matrix saves, show added / removed keys instead of two long lists. */
const grantDiff = (before, after) => {
  try {
    const b = new Set((JSON.parse(before || '[]') || []).map(x => x.key));
    const a = new Set((JSON.parse(after || '[]') || []).map(x => x.key));
    return { added: [...a].filter(k => !b.has(k)), removed: [...b].filter(k => !a.has(k)) };
  } catch (_e) {
    return null;
  }
};

const AuditTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ top: 200 });

  const load = useCallback(async f => {
    setLoading(true);
    try {
      setRows(await AccessService.getAudit(f));
    } catch (_e) {
      // toast shown by httpInterceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load({ top: 200 });
  }, [load]);

  const columns = [
    { title: 'When (local)', dataIndex: 'occurredOnUtc', width: 180, render: v => new Date(v.endsWith('Z') ? v : `${v}Z`).toLocaleString() },
    { title: 'Actor', dataIndex: 'actorDisplay', width: 220 },
    { title: 'Action', dataIndex: 'action', width: 170, render: v => <Tag>{v}</Tag> },
    { title: 'Target', key: 'target', width: 140, render: (_, r) => `${r.targetType}${r.targetId ? ` #${r.targetId}` : ''}` },
    { title: 'Outcome', dataIndex: 'outcome', width: 90 },
    { title: 'Correlation', dataIndex: 'correlationId', ellipsis: true },
  ];

  const expandedRowRender = r => {
    if (r.action === 'ROLE_MATRIX_SAVE') {
      const diff = grantDiff(r.beforeJson, r.afterJson);
      if (diff) {
        return (
          <div className="row">
            <div className="col-md-6">
              <strong>Added ({diff.added.length})</strong>
              <pre className="ac-json">{diff.added.join('\n') || '—'}</pre>
            </div>
            <div className="col-md-6">
              <strong>Removed ({diff.removed.length})</strong>
              <pre className="ac-json">{diff.removed.join('\n') || '—'}</pre>
            </div>
          </div>
        );
      }
    }
    return (
      <div className="row">
        <div className="col-md-6">
          <strong>Before</strong>
          <pre className="ac-json">{pretty(r.beforeJson)}</pre>
        </div>
        <div className="col-md-6">
          <strong>After</strong>
          <pre className="ac-json">{pretty(r.afterJson)}</pre>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="ac-toolbar">
        <DatePicker.RangePicker
          onChange={range =>
            setFilter(f => ({
              ...f,
              fromUtc: range?.[0] ? range[0].startOf('day').toDate().toISOString() : undefined,
              toUtc: range?.[1] ? range[1].endOf('day').toDate().toISOString() : undefined,
            }))
          }
        />
        <Input placeholder="Actor" allowClear style={{ width: 200 }} onChange={e => setFilter(f => ({ ...f, actor: e.target.value }))} />
        <Select placeholder="Action" allowClear style={{ width: 190 }} options={ACTIONS.map(a => ({ value: a, label: a }))} onChange={v => setFilter(f => ({ ...f, action: v }))} />
        <Select placeholder="Target" allowClear style={{ width: 140 }} options={TARGETS.map(t => ({ value: t, label: t }))} onChange={v => setFilter(f => ({ ...f, targetType: v }))} />
        <Button type="primary" onClick={() => load(filter)} loading={loading}>
          Search
        </Button>
      </div>
      <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={rows} expandable={{ expandedRowRender }} pagination={{ pageSize: 25 }} scroll={{ x: 900 }} />
    </>
  );
};

export default AuditTab;
